import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Plus, Minus, Trash2, ChefHat } from "lucide-react";
import { staffApi, STATUS_META, money, timeSince } from "./staffApi";
import { usePinPrompt } from "./PinModal";

const SPICES = [null, "Mild", "Medium", "Hot", "Extra Hot"];
const Btn = ({ testId, onClick, color = "#D4AF37", textColor = "#000", children, disabled, outline }) => (
  <button data-testid={testId} onClick={onClick} disabled={disabled}
    className="w-full rounded-2xl py-4 text-base font-black tracking-wide disabled:opacity-40 border"
    style={outline ? { borderColor: `${color}88`, color } : { background: color, color: textColor, borderColor: color }}>
    {children}
  </button>
);

export const OrderDetail = ({ orderId, staff, onBack }) => {
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState(null);
  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);
  const [menu, setMenu] = useState([]);
  const [search, setSearch] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [team, setTeam] = useState([]);
  const [onShift, setOnShift] = useState([]);
  const [askPin, pinEl] = usePinPrompt();
  const dirtyRef = useRef(false);

  const refresh = useCallback(() => staffApi.get("/staff/board").then((r) => {
    setTeam(r.data.staff || []);
    setOnShift(r.data.on_shift || []);
    const o = r.data.orders.find((x) => x.order_id === orderId);
    if (o) {
      setOrder(o);
      setItems((prev) => (prev === null || !dirtyRef.current ? o.items.map((i) => ({ ...i })) : prev));
      setNotes((prev) => (prev === "" && o.waiter_notes ? o.waiter_notes : prev));
    }
  }).catch(() => {}), [orderId]);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    staffApi.get("/staff/menu").then((r) => setMenu(r.data)).catch(() => {});
    return () => clearInterval(t);
  }, [refresh]);

  if (!order || items === null) return <p className="p-8 text-white/50 text-lg">Loading order…</p>;

  const meta = STATUS_META[order.status] || STATUS_META.completed;
  const act = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); setOrder(r.data); setItems(r.data.items.map((i) => ({ ...i }))); setDirty(false); if (ok) toast.success(ok); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Something went wrong");
    }
    setBusy(false);
  };
  // Accountable actions: session if signed in, otherwise the PIN pad — right here, no reload
  const actPin = async (path, body, ok, title) => {
    let pin = null;
    if (!staff) {
      pin = await askPin(title || "Enter your PIN", "This action is recorded under your name");
      if (!pin) return;
    }
    await act(() => staffApi.post(path, { ...(body || {}), ...(pin ? { pin } : {}) }), ok);
  };
  const saveEdits = () => act(() => staffApi.post(`/staff/orders/${orderId}/update`, { items, waiter_notes: notes || null }), "Changes saved");
  const setQty = (idx, q) => {
    const next = items.map((i, n) => (n === idx ? { ...i, qty: Math.max(0, q) } : i)).filter((i) => i.qty > 0);
    setItems(next); setDirty(true);
  };
  const cycleSpice = (idx) => {
    setItems(items.map((i, n) => (n === idx ? { ...i, spice: SPICES[(SPICES.indexOf(i.spice || null) + 1) % SPICES.length] } : i)));
    setDirty(true);
  };
  const toggleUnavailable = (idx) => { setItems(items.map((i, n) => (n === idx ? { ...i, unavailable: !i.unavailable } : i))); setDirty(true); };
  const addDish = (d) => {
    const ex = items.findIndex((i) => i.slug === d.slug && !i.unavailable);
    setItems(ex >= 0 ? items.map((i, n) => (n === ex ? { ...i, qty: i.qty + 1 } : i))
      : [...items, { slug: d.slug, name: d.name, qty: 1, price: d.price || 0, spice: null, note: null, unavailable: false }]);
    setDirty(true); setSearch("");
  };
  const isMgr = staff && (staff.role === "manager" || staff.role === "owner");
  const canTouch = staff ? (isMgr || !order.assigned_staff_id || order.assigned_staff_id === staff.staff_id) : false;
  const active = !["completed", "cancelled"].includes(order.status);
  const releasable = active && !["waiting_for_waiter", "waiter_reviewing"].includes(order.status);
  const editable = canTouch && ["waiting_for_waiter", "waiter_reviewing"].includes(order.status);
  const live = items.filter((i) => !i.unavailable);
  const results = search.trim() ? menu.filter((d) => d.name.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 5) : [];

  return (
    <div data-testid="order-detail" className="p-4 pb-24 max-w-2xl mx-auto">
      <div className="flex items-center justify-between">
        <button data-testid="order-back" onClick={onBack} className="flex items-center gap-2 text-white/70 font-black text-sm py-2 pr-3">
          <ArrowLeft size={18} /> ALL TABLES
        </button>
        <span className="rounded-full px-4 py-2 text-sm font-black tracking-wider" style={{ color: meta.color, background: meta.bg }}>{meta.label}</span>
      </div>
      <div className="mt-3 rounded-2xl border border-white/10 bg-[#141419] p-4">
        <div className="flex items-baseline justify-between">
          <p className="text-4xl font-black">TABLE {order.table}</p>
          <p className="text-white/45 text-sm font-bold">{order.order_id}</p>
        </div>
        <p className="text-white/55 text-sm mt-1.5 font-semibold">
          {timeSince(order.created_at)}{order.guests ? ` · ${order.guests} guests` : ""}{order.customer_name ? ` · ${order.customer_name}` : ""}
          {order.assigned_name ? ` · VeerGi: ${order.assigned_name}` : " · unassigned"}
        </p>
        {order.allergies && (
          <div data-testid="order-allergy-banner" className="mt-3 rounded-xl border-2 border-[#FF5A4E] bg-[#FF5A4E]/15 p-3">
            <p className="flex items-center gap-2 text-[#FF5A4E] font-black text-lg"><AlertTriangle size={20} /> ALLERGY — {order.allergies}</p>
            <label className="flex items-center gap-2.5 mt-2 text-sm font-bold text-white/85">
              <input data-testid="order-allergy-confirm" type="checkbox" checked={!!order.allergies_confirmed}
                onChange={(e) => act(() => staffApi.post(`/staff/orders/${orderId}/update`, { allergies_confirmed: e.target.checked }))}
                className="w-6 h-6 accent-[#FF5A4E]" />
              I confirmed the allergy with the table
            </label>
          </div>
        )}
        {order.followup_of && (
          <p data-testid="order-followup" className="mt-2.5 text-sm text-[#F0B429] font-black border-l-4 border-[#F0B429] pl-3">↩ SAME TABLE — 2ND TICKET · same party as {order.followup_of}, still seated</p>
        )}
        {order.special_instructions && (
          <p data-testid="order-special" className="mt-2.5 text-sm text-[#F0B429] font-semibold border-l-4 border-[#F0B429] pl-3">“{order.special_instructions}”</p>
        )}
      </div>

      <div className="mt-3 rounded-2xl border border-white/10 bg-[#141419] p-4">
        <p className="text-xs font-black tracking-[0.25em] text-[#D4AF37]">ORDER ITEMS</p>
        <div className="divide-y divide-white/8">
          {items.map((i, idx) => (
            <div key={`${i.slug}-${idx}`} data-testid={`order-item-${i.slug}`} className={`py-3.5 ${i.unavailable ? "opacity-45" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <p className={`text-lg font-bold flex-1 min-w-0 ${i.unavailable ? "line-through" : ""}`}>
                  {i.name}
                  {i.round >= 2 && (
                    <span data-testid={`item-round-tag-${i.slug}`} className="ml-2 text-[10px] font-black text-[#F0B429] border border-[#F0B429]/50 rounded px-1.5 py-0.5 align-middle">
                      R{i.round}{i.added_at ? ` · ${timeSince(i.added_at)}` : ""}
                    </span>
                  )}
                </p>
                <p className="font-black text-[#D4AF37] shrink-0">{money(i.qty * i.price)}</p>
              </div>
              {i.note && <p className="text-sm text-white/55 mt-0.5">“{i.note}”</p>}
              {editable ? (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button data-testid={`item-minus-${i.slug}`} onClick={() => setQty(idx, i.qty - 1)} className="w-12 h-12 rounded-xl bg-[#1D1D23] border border-white/15 flex items-center justify-center"><Minus size={18} /></button>
                  <span data-testid={`item-qty-${i.slug}`} className="w-8 text-center text-xl font-black">{i.qty}</span>
                  <button data-testid={`item-plus-${i.slug}`} onClick={() => setQty(idx, i.qty + 1)} className="w-12 h-12 rounded-xl bg-[#1D1D23] border border-white/15 flex items-center justify-center"><Plus size={18} /></button>
                  <button data-testid={`item-spice-${i.slug}`} onClick={() => cycleSpice(idx)}
                    className="h-12 px-4 rounded-xl border border-[#E25822]/50 text-[#E25822] text-sm font-black">🌶 {i.spice || "No spice set"}</button>
                  <button data-testid={`item-86-${i.slug}`} onClick={() => toggleUnavailable(idx)}
                    className={`h-12 px-4 rounded-xl border text-sm font-black ${i.unavailable ? "border-[#2ECC71]/60 text-[#2ECC71]" : "border-white/20 text-white/60"}`}>
                    {i.unavailable ? "RESTORE" : "MARK 86'D"}
                  </button>
                  <button data-testid={`item-remove-${i.slug}`} onClick={() => setQty(idx, 0)} aria-label="Remove"
                    className="w-12 h-12 rounded-xl border border-[#FF5A4E]/50 text-[#FF5A4E] flex items-center justify-center"><Trash2 size={17} /></button>
                </div>
              ) : (
                <p className="text-sm text-white/55 font-bold mt-1">× {i.qty}{i.spice ? ` · 🌶 ${i.spice}` : ""}{i.unavailable ? " · 86'D" : ""}</p>
              )}
            </div>
          ))}
        </div>
        {editable && (
          <div className="mt-2">
            <input data-testid="order-add-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="＋ Add an item — type to search the menu"
              className="w-full bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3.5 text-base focus:outline-none focus:border-[#D4AF37]/70" />
            {results.map((d) => (
              <button key={d.slug} data-testid={`add-dish-${d.slug}`} onClick={() => addDish(d)}
                className="w-full flex justify-between items-center px-4 py-3 text-left border-b border-white/8 text-base font-semibold hover:bg-white/5">
                <span>{d.name}</span><span className="text-[#D4AF37] font-black">{money(d.price)}</span>
              </button>
            ))}
            <textarea data-testid="order-waiter-notes" value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
              placeholder="VeerGi notes for the kitchen…" rows={2}
              className="w-full mt-3 bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3 text-base focus:outline-none focus:border-[#D4AF37]/70" />
            {dirty && <div className="mt-2"><Btn testId="order-save" onClick={saveEdits} disabled={busy} color="#5AB0FF">SAVE CHANGES</Btn></div>}
          </div>
        )}
        <div className="flex justify-between items-center border-t border-white/10 mt-3 pt-3 text-sm font-bold text-white/60">
          <span>{order.pos_total != null ? "Till price — the till's number, quoted back" : "Priced at the till"}</span>
          <span data-testid="order-till-price" className="text-2xl font-black text-[#D4AF37]">{order.pos_total != null ? money(order.pos_total) : "—"}</span>
        </div>
      </div>

      <div className="mt-4 space-y-2.5">
        {staff && !canTouch && (
          <p data-testid="order-locked-banner" className="rounded-2xl border border-[#F0B429]/50 bg-[#F0B429]/10 text-[#F0B429] font-black text-sm p-4">
            This table belongs to {order.assigned_name} — they can PASS it to you, or a manager can force-transfer.
          </p>
        )}
        {order.transfer_pending && (
          <div data-testid="order-transfer-banner" className="rounded-2xl border-2 border-[#5AB0FF]/60 bg-[#5AB0FF]/10 p-4">
            <p className="font-black text-[#5AB0FF] text-base">⇄ PASS TO {order.transfer_pending.to_name} — waiting for their PIN</p>
            <p className="text-sm text-white/55 font-bold mt-1">Responsibility only moves when {order.transfer_pending.to_name} confirms. Offered by {order.transfer_pending.requested_by}.</p>
            <div className="flex gap-2 mt-3">
              <button data-testid="order-transfer-accept" disabled={busy}
                onClick={() => actPin(`/staff/orders/${orderId}/transfer-accept`, {}, `Table ${order.table} is yours now`, `Accept Table ${order.table} — ${order.transfer_pending.to_name}'s PIN`)}
                className="flex-1 rounded-xl bg-[#5AB0FF] text-black font-black py-3.5 text-sm tracking-wide">I'M {String(order.transfer_pending.to_name || "").toUpperCase()} — ACCEPT (PIN)</button>
              <button data-testid="order-transfer-cancel" disabled={busy}
                onClick={() => actPin(`/staff/orders/${orderId}/transfer-cancel`, {}, "Transfer cancelled")}
                className="rounded-xl border border-white/20 text-white/70 font-black py-3.5 px-4 text-sm">CANCEL</button>
            </div>
          </div>
        )}
        {isMgr && team.length > 0 && active && (
          <div className="flex items-center gap-2">
            <select data-testid="order-reassign-select" defaultValue=""
              onChange={(e) => {
                if (!e.target.value) return;
                const reason = window.prompt("Force-transfer reason (recorded in the audit):");
                if (!reason || reason.trim().length < 3) { toast.error("A short reason is required"); e.target.value = ""; return; }
                act(() => staffApi.post(`/staff/orders/${orderId}/reassign`, { staff_id: e.target.value, reason }), "Force-transferred");
              }}
              className="flex-1 bg-[#1A1A20] border border-white/15 rounded-xl px-3 py-3.5 text-sm font-bold">
              <option value="" disabled>FORCE-TRANSFER TO… (manager, reason required)</option>
              {team.filter((t) => t.staff_id !== order.assigned_staff_id).map((t) => (
                <option key={t.staff_id} value={t.staff_id}>{t.name} ({t.role === "waiter" ? "VeerGi" : t.role})</option>
              ))}
            </select>
          </div>
        )}
        {!order.assigned_staff_id && active && order.status !== "waiting_for_waiter" && (
          <Btn testId="order-claim" onClick={() => actPin(`/staff/orders/${orderId}/claim`, {}, `Table ${order.table} is yours — your name is on it`, `Accept Table ${order.table}`)} disabled={busy}>
            ACCEPT TABLE {order.table} — MAKE IT MINE
          </Btn>
        )}
        {order.status === "waiting_for_waiter" && (!staff || canTouch) && <Btn testId="order-accept" onClick={() => actPin(`/staff/orders/${orderId}/accept`, {}, "Table accepted — the whole visit is yours", `Accept Table ${order.table}`)} disabled={busy}>ACCEPT ORDER — TABLE {order.table}</Btn>}
        {editable && order.status === "waiter_reviewing" && (
          <Btn testId="order-confirm-open"
            onClick={() => (dirty ? toast.error("Save your changes first")
              : order.allergies && !order.allergies_confirmed ? toast.error("Confirm the allergy with the table first — tap the red checkbox")
              : setConfirming(true))}
            disabled={busy} color="#2ECC71">REVIEW & CONFIRM ORDER</Btn>
        )}
        {canTouch && order.new_round_pending && (
          <Btn testId="order-confirm-round"
            onClick={() => act(() => staffApi.post(`/staff/orders/${orderId}/confirm-round`), `Round ${order.rounds_count} confirmed — send the new items to the kitchen`)}
            disabled={busy} color="#F0B429">
            CONFIRM ROUND {order.rounds_count} — {items.filter((i) => i.round === order.rounds_count && !i.unavailable).length} NEW ITEM{items.filter((i) => i.round === order.rounds_count && !i.unavailable).length === 1 ? "" : "S"}
          </Btn>
        )}
        {!["completed", "cancelled"].includes(order.status) && (
          <button data-testid="detail-change-table"
            onClick={() => {
              const nt = window.prompt(`Move this order to which table? (now ${order.table})`);
              if (!nt || !nt.trim()) return;
              if (!window.confirm(`Move table ${order.table} → ${nt.trim()}? The POS ticket updates right away.`)) return;
              staffApi.post(`/staff/orders/${orderId}/change-table`, { table: nt.trim() })
                .then((r) => { toast.success(`Now table ${r.data.table}${r.data.pos === "sent" ? " — POS ticket updated ✓" : r.data.pos === "not_sent_yet" ? "" : " — POS update queued"}`); refresh(); })
                .catch((er) => toast.error(typeof er.response?.data?.detail === "string" ? er.response.data.detail : "Could not change table"));
            }}
            className="w-full min-h-[44px] rounded-xl border border-white/25 text-white/80 font-black text-xs tracking-wider mb-3">
            CHANGE TABLE (now {order.table})
          </button>
        )}
        {["confirmed", "pos_failed", "sent_to_pos"].includes(order.status) && (
          <div data-testid="manual-pos-panel" className="rounded-2xl border-2 border-dashed border-[#5AB0FF]/60 bg-[#5AB0FF]/10 p-4">
            {(() => {
              const ps = order.pos_sync || {};
              const sent = ps.state === "sent";
              const failed = ps.state === "failed";
              const manual = ps.state === "manual";
              return (
                <div data-testid="pos-sync-status" className={`mb-3 rounded-xl px-3 py-2.5 border text-sm font-black flex items-center justify-between gap-2 flex-wrap ${
                  sent || manual ? "border-[#10B981]/60 text-[#10B981] bg-[#10B981]/10" : failed ? "border-[#FF5A4E]/60 text-[#FF5A4E] bg-[#FF5A4E]/10" : "border-[#F59E0B]/50 text-[#F59E0B] bg-[#F59E0B]/10"}`}>
                  <span>
                    POS SYNC: {sent ? `SENT ✓ · POS #${ps.pos_order_id}` : manual ? `ENTERED IN POS MANUALLY ✓ · ${ps.resolved_by || "staff"}` : failed ? "FAILED — WON'T AUTO-RETRY" : "PENDING"}
                    {isMgr && !sent && !manual && ps.error_code && (
                      <span className="text-white/45 font-bold" data-testid="pos-sync-error-detail"> · {ps.error_code}
                        {!failed && ["unreachable", "pos_5xx"].includes(ps.error_code) && ` · auto-retry ${ps.attempts || 0}/8`}
                      </span>
                    )}
                    {isMgr && !sent && !manual && order.pos_last_reply && (
                      <span className="block text-white/45 font-bold text-[11px] mt-1" data-testid="pos-last-reply">
                        POS said: HTTP {order.pos_last_reply.http_status ?? "no answer"} · {order.pos_last_reply.body}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    {failed && (
                      <button data-testid="pos-sync-resolve" disabled={busy}
                        onClick={() => act(() => staffApi.post(`/staff/pos-sync/${orderId}/resolve`), "Marked as entered in POS — alarm cleared")}
                        className="rounded-lg border border-current px-3 py-1.5 text-xs font-black">ENTERED IN POS MANUALLY</button>
                    )}
                    {!sent && !manual && (
                      <button data-testid="pos-sync-retry" disabled={busy}
                        onClick={() => act(() => staffApi.post(`/staff/pos-sync/${orderId}/retry`).then(() => staffApi.get("/staff/board").then((r) => ({ data: r.data.orders.find((x) => x.order_id === orderId) }))), "Sync retried")}
                        className="rounded-lg border border-current px-3 py-1.5 text-xs font-black">
                        {order.status === "pos_failed" ? "NOT IN KITCHEN — RETRY" : "RETRY SYNC"}
                      </button>
                    )}
                  </span>
                </div>
              );
            })()}
            <p className="flex items-center gap-2 font-black text-[#5AB0FF] text-base"><ChefHat size={18} /> {order.pos_sync?.state === "sent" ? "SENT TO VEERGIGALLA — manual entry below is the backup copy" : "TEMPORARY WORKFLOW — MANUAL POS ENTRY (fallback until sync succeeds)"}</p>
            <p className="text-sm text-white/65 mt-1">{order.pos_sync?.state === "sent" ? "The POS already has this order. Tap PREPARING once the kitchen starts." : "Type this order into the POS now, then tap PREPARING."}</p>
            <div className="mt-2 rounded-xl bg-black/40 p-3 font-mono text-base leading-relaxed">
              {live.map((i) => <p key={i.slug}>{i.qty}× {i.name}{i.spice ? ` (${i.spice})` : ""}{i.note ? ` — ${i.note}` : ""}</p>)}
              {order.waiter_notes && <p className="text-[#F0B429]">NOTE: {order.waiter_notes}</p>}
            </div>
            <div className="mt-3"><Btn testId="order-preparing" onClick={() => act(() => staffApi.post(`/staff/orders/${orderId}/status`, { status: "preparing" }))} disabled={busy} color="#5AB0FF">ENTERED IN POS — KITCHEN PREPARING</Btn></div>
          </div>
        )}
        {canTouch && order.status === "preparing" && (
          <p data-testid="order-awaiting-kitchen" className="text-sm text-white/55 font-bold">The kitchen marks READY — the board sounds the moment it's up. You mark SERVED once it's on the table.</p>
        )}
        {canTouch && ["preparing", "ready"].includes(order.status) && <Btn testId="order-served" onClick={() => act(() => staffApi.post(`/staff/orders/${orderId}/status`, { status: "served" }))} disabled={busy} color="#3FD8C2">SERVED TO TABLE</Btn>}
        {canTouch && active && !order.transfer_pending && order.assigned_staff_id && onShift.filter((t) => t.staff_id !== order.assigned_staff_id).length > 0 && (
          <div className="flex items-center gap-2">
            <select data-testid="order-pass-select" defaultValue=""
              onChange={(e) => e.target.value && act(() => staffApi.post(`/staff/orders/${orderId}/pass`, { to_staff_id: e.target.value }), "Offered — they confirm with their PIN")}
              className="flex-1 bg-[#1A1A20] border border-[#5AB0FF]/40 rounded-xl px-3 py-3.5 text-sm font-bold text-[#5AB0FF]">
              <option value="" disabled>⇄ PASS TABLE {order.table} TO… (they confirm with their PIN)</option>
              {onShift.filter((t) => t.staff_id !== order.assigned_staff_id).map((t) => (
                <option key={t.staff_id} value={t.staff_id}>{t.name} ({t.role === "waiter" ? "VeerGi" : t.role})</option>
              ))}
            </select>
          </div>
        )}
        {releasable && !order.new_round_pending && !order.transfer_pending && (
          <Btn testId="order-release"
            onClick={() => window.confirm(`Guests left Table ${order.table}? This closes visit ${order.order_id} — the next party is a new visit.`) &&
              actPin(`/staff/orders/${orderId}/release`, {}, `Table ${order.table} released — ready for the next party`, `Guests left — clear Table ${order.table}`)}
            disabled={busy} color="#9AA0A6" outline>
            GUESTS LEFT / CLEAR TABLE {order.table}
          </Btn>
        )}
        {canTouch && active && (
          <Btn testId="order-cancel" onClick={() => window.confirm(`Cancel order ${order.order_id}?`) && act(() => staffApi.post(`/staff/orders/${orderId}/cancel`, { status: "cancelled", reason: "staff cancel" }), "Order cancelled")} disabled={busy} color="#FF5A4E" outline>CANCEL ORDER</Btn>
        )}
      </div>

      {order.audit?.length > 0 && (
        <div className="mt-5 rounded-2xl border border-white/10 bg-[#121216] p-4">
          <p className="text-xs font-black tracking-[0.25em] text-white/40">HISTORY</p>
          {order.audit.slice(-8).reverse().map((a, i) => (
            <p key={i} className="text-sm text-white/50 mt-1.5">{String(a.ts).slice(11, 16)} · <b className="text-white/75">{a.staff}</b> {a.action}{a.detail ? ` — ${a.detail}` : ""}</p>
          ))}
        </div>
      )}

      {pinEl}
      {confirming && (
        <div data-testid="confirm-modal" className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setConfirming(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-[#16161B] border border-[#2ECC71]/50 p-5 max-h-[85vh] overflow-y-auto">
            <p className="text-xs font-black tracking-[0.3em] text-[#2ECC71]">FINAL CHECK · TABLE {order.table}</p>
            <div className="mt-3 divide-y divide-white/8">
              {live.map((i) => (
                <div key={i.slug} className="py-2.5 flex justify-between text-base">
                  <span className="font-bold">{i.qty}× {i.name}{i.spice ? ` · 🌶 ${i.spice}` : ""}</span>
                  <span className="font-black text-[#D4AF37] shrink-0">{money(i.qty * i.price)}</span>
                </div>
              ))}
            </div>
            {order.allergies && <p className="mt-3 text-[#FF5A4E] font-black text-base flex items-center gap-2"><AlertTriangle size={17} /> ALLERGY: {order.allergies}</p>}
            <p className="mt-3 text-right text-sm font-bold text-white/50">The till prices this order.</p>
            <div className="mt-4 space-y-2">
              <Btn testId="order-confirm-send" onClick={() => { setConfirming(false); act(() => staffApi.post(`/staff/orders/${orderId}/confirm`), "Sent — enter it in the POS now"); }} disabled={busy} color="#2ECC71">CONFIRM & SEND TO POS/KITCHEN</Btn>
              <Btn testId="order-confirm-back" onClick={() => setConfirming(false)} outline color="#FFFFFF">GO BACK</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
